import s from './Leaderboard.module.sass';

import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { AnimatedEmoji, Button, Header, Placeholder, ScrollView } from '$uikit';
import { leaderboardActions, selectLeaderboard } from '$store/leaderboard';
import { Loader, UnitLink, UnitName, UnitPhoto, UserCell } from '$shared/components';
import {
  useCurrentUserInfo,
  useTimer,
  useTranslator,
  useUserInfo,
  useWikiLink,
} from '$hooks';
import { format, formatCurrency } from '$utils';
import { IcInfo28, IcList28 } from '$assets';
import { goTo } from '$navigation/helper';
import { LEADERBOARD_DETAILS, LEADERBOARDS_HISTORY } from '$shared/constants/pages';

const CountdownDates: FC<{ datesText: string; endAt: number }> = ({
  datesText,
  endAt,
}) => {
  const t = useTranslator();
  const endAtMs = endAt * 1000;
  const isEndingSoon =
    endAt > 0 && endAtMs - Date.now() > 0 && endAtMs - Date.now() < 24 * 60 * 60 * 1000;
  const countdown = useTimer(isEndingSoon ? endAtMs : 0);

  return (
    <>
      {t('leaderboard_description_with_date', {
        dates: isEndingSoon ? `${datesText} (${countdown})` : datesText,
      })}
    </>
  );
};

const TopModel: FC<{ mid: number; rank: number; prize: number; currency: string }> = ({
  mid,
  rank,
  prize,
  currency,
}) => {
  const user = useUserInfo(mid);

  const isSmall = rank > 0;

  return (
    <UnitLink
      unit={user}
      className={cn({
        [s.topModelsItem]: true,
        [s.topModelsItemSmall]: isSmall,
        [s.topModelsItemGold]: rank === 0,
        [s.topModelsItemSilver]: rank === 1,
        [s.topModelsItemBronze]: rank === 2,
      })}
    >
      <div className={s.topModelsItemPrize}>{formatCurrency(prize, currency)}</div>
      <div className={s.topModelsItemPhoto}>
        <UnitPhoto
          unit={user}
          size={isSmall ? 'large' : 'extra-large'}
          nftIndicator={false}
          onlineIndicator={false}
        />
        <div className={s.topModelsItemRank}>{rank + 1}</div>
      </div>
      <div className={s.topModelsItemName}>
        <UnitName unit={user} />
      </div>
    </UnitLink>
  );
};

const OtherModel: FC<{
  mid: number;
  rank: number;
  prize: number;
  currency: string;
  skipPadding?: boolean;
  highlighted?: boolean;
}> = ({ mid, rank, prize, currency, skipPadding = false, highlighted = false }) => {
  const cell = (
    <UserCell
      objectId={mid}
      before={<div className={s.otherModelRank}>{rank + 1}</div>}
      isFollowBtnShown={false}
      after={<div className={s.otherModelPrize}>{formatCurrency(prize, currency)}</div>}
      skipPadding={skipPadding}
    />
  );

  if (highlighted) {
    return <div className={s.highlightedPosition}>{cell}</div>;
  }

  return cell;
};

export const Leaderboard: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const {
    id: leaderboardId,
    loadingStatus,
    memberIds,
    prizes,
    currency,
    myRank,
    startAt,
    endAt,
    title,
    wikiLink,
    type,
  } = useSelector(selectLeaderboard);
  const wiki = useWikiLink(wikiLink ?? '/docs-and-terms/ton-girls-contest');
  const curUser = useCurrentUserInfo();
  const isModel = curUser && curUser.verified === 2;

  const handleLoad = () => {
    dispatch(leaderboardActions.load());
  };

  useEffect(() => {
    handleLoad();
  }, []);

  const isStarted = useMemo(() => {
    return Date.now() >= startAt * 1000;
  }, [startAt]);

  const datesText = useMemo(() => {
    if (!startAt || !endAt) return '';

    const start = new Date(startAt * 1000);
    const end = new Date(endAt * 1000);

    const startMonth = format(start, 'LLLL');
    const endMonth = format(end, 'LLLL');

    if (startMonth === endMonth) {
      return `${startMonth} ${format(start, 'd')}–${format(end, 'd')}`;
    }

    return `${startMonth} ${format(start, 'd')} – ${endMonth} ${format(end, 'd')}`;
  }, [startAt, endAt]);

  function renderTop() {
    if (!isStarted) {
      return <Placeholder>{t('leaderboard_not_started')}</Placeholder>;
    }

    return (
      <>
        <div className={s.topModels}>
          <TopModel mid={memberIds[1]} rank={1} prize={prizes[1]} currency={currency} />
          <TopModel mid={memberIds[0]} rank={0} prize={prizes[0]} currency={currency} />
          <TopModel mid={memberIds[2]} rank={2} prize={prizes[2]} currency={currency} />
        </div>
        {memberIds.slice(3).map((mid, i) => {
          const rank = i + 3;
          const changed =
            (i > 0 && prizes[rank] !== prizes[rank + 1]) || rank === memberIds.length - 1;
          const isMyPosition = isModel && curUser && mid === curUser.id;
          return (
            <div key={mid}>
              <OtherModel
                mid={mid}
                rank={rank}
                prize={prizes[rank]}
                currency={currency}
                highlighted={isMyPosition}
              />
              {/*{changed && <div className={s.topSeparator}>Top {rank + 1}</div>}*/}
            </div>
          );
        })}
      </>
    );
  }

  function renderContent() {
    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          centered
          actions={<Button onClick={handleLoad}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (loadingStatus === 'loading' && !memberIds.length) {
      return <Loader isWrapped />;
    }

    return (
      <div>
        <Placeholder
          iconSize={84}
          icon={<AnimatedEmoji emoji="👑" loop size={84} />}
          title={title ?? 'TON Girls Contest'}
          compact
          actions={
            <Button size="small" href={wiki} target="_blank">
              {t('invite_bonus_detaild')}
            </Button>
          }
        >
          <CountdownDates datesText={datesText} endAt={endAt} />
        </Placeholder>
        {renderTop()}
      </div>
    );
  }

  function renderFooter() {
    if (!curUser || loadingStatus !== '' || !isModel) {
      return null;
    }

    if (myRank === -1) {
      return (
        <Button
          onClick={() => {
            dispatch(leaderboardActions.join(leaderboardId));
          }}
        >
          {t('leaderboard_join')}
        </Button>
      );
    }

    const isInTop = memberIds.includes(curUser.id);
    if (isInTop) {
      return null;
    }

    return (
      <OtherModel
        mid={curUser.id}
        rank={myRank}
        prize={prizes[myRank] || 0}
        currency={currency}
        skipPadding
        highlighted
      />
    );
  }

  const rightIcons = [
    {
      icon: <IcList28 />,
      onClick: () => goTo(LEADERBOARDS_HISTORY),
    },
  ];

  if (isModel && myRank >= 0) {
    rightIcons.unshift({
      icon: <IcInfo28 />,
      onClick: () => goTo(LEADERBOARD_DETAILS, { id: leaderboardId }),
    });
  }

  return (
    <>
      <Header showBackButton isTransparent rightIcons={rightIcons} />
      <ScrollView footer={renderFooter()}>{renderContent()}</ScrollView>
    </>
  );
};
